import React from 'react';
import { AlertTriangle, ArrowRight, Plus } from 'lucide-react';
import { RobbieMark, TruncatedText } from '../../ui';
import { ChangeRibbonGroup, CHANGE_FIELD_LABEL } from '../../../hooks/outreachActionModel';

/**
 * Terms that moved, rendered inline where they moved: the ribbon sits
 * immediately after the reply that caused the movement — inside the message
 * scroll, so it leaves the screen with the reply rather than pinning itself
 * above the composer.
 *
 * Every value is a stored ledger entry — the supplier's own previous and new
 * figure, plus their own caveat when they stated one. Nothing is computed into
 * existence here. A supplier's sentence can be any length, so every value that
 * clips carries the full text in an instant tooltip — measured, so text that
 * fits stays quiet.
 */
interface ChangeRibbonProps {
  group: ChangeRibbonGroup;
}

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-s2 rounded-pill border border-robbie-border-soft bg-surface px-[9px] py-[3px] text-meta text-ink-secondary">
    {children}
  </span>
);

const ChangeRibbon: React.FC<ChangeRibbonProps> = ({ group }) => (
  <div className="flex shrink-0 flex-wrap items-center gap-s3 self-stretch rounded-[9px] border border-robbie-border bg-ai-chip px-[12px] py-s3">
    <RobbieMark size="xxs" aria-label="Robbie read these changes out of the reply" />
    <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-robbie-ink">Terms moved</span>
    {group.changes.map((change, idx) => (
      <Chip key={`${change.field}-${idx}`}>
        <span className="shrink-0 text-ink-muted">{CHANGE_FIELD_LABEL[change.field]}</span>
        <TruncatedText className="max-w-[140px] font-mono line-through" text={change.previousValue} side="bottom" />
        <ArrowRight className="h-[11px] w-[11px] shrink-0 text-ink-muted" />
        <TruncatedText
          className="max-w-[160px] font-mono font-semibold text-success-ink"
          text={change.newValue}
          side="bottom"
        />
      </Chip>
    ))}
    {group.commitments.map((commitment, idx) => (
      <Chip key={`commitment-${idx}`}>
        <Plus className="h-[11px] w-[11px] shrink-0 text-success-ink" />
        <TruncatedText className="max-w-[320px]" text={commitment.statement} side="bottom" />
      </Chip>
    ))}
    {group.changes
      .filter((change) => Boolean(change.condition))
      .map((change, idx) => (
        <TruncatedText
          key={`condition-${idx}`}
          className="max-w-[280px] text-[11px] leading-[1.4] text-robbie-ink"
          text={change.condition as string}
          side="bottom"
        />
      ))}
  </div>
);

/**
 * Extraction fell over on this reply. Said out loud in the ribbon's slot
 * rather than left silent — the buyer needs to know the numbers below are not
 * on file, and that the message itself still counts.
 */
export const ExtractionFailedRibbon: React.FC = () => (
  <div className="flex shrink-0 items-center gap-s3 self-stretch rounded-[9px] border border-warn-border bg-warn-bg px-[12px] py-s3">
    <AlertTriangle className="h-[13px] w-[13px] shrink-0 text-warn-ink" />
    <span className="text-meta leading-[1.4] text-warn-ink">
      I couldn&rsquo;t read the numbers in this reply — the message itself is still the source of truth.
    </span>
  </div>
);

export default ChangeRibbon;
